<template>
  <b-modal :modelValue="deal !== null"
           header-class="d-none"
           footer-class="d-flex justify-content-between"
           @update:modelValue="$event === false && (deal = null)">
    <template #default>
      <h5 class="text-capitalize-first">{{ helper.trans('labels.change_payout_amount') }}</h5>
      <div class="d-flex align-items-start">
        <FormInputWrapper class="flex-grow-1" v-slot="parentProps" :errors="errors">
          <input type="text"
                 class="form-control"
                 :class="parentProps.class"
                 v-model="form.amount"
                 @keydown.enter.prevent="onOkClick()">
        </FormInputWrapper>
        <span class="input-group-text">{{ deal?.takerSum.currency }}</span>
      </div>
    </template>
    <template #footer>
      <button type="button" @click="deal = null" class="btn btn-outline-dark btn-sm text-capitalize-first">
        {{ helper.trans('button.cancel') }}
      </button>
      <LoaderWrapper ref="loader" :zoom="0.5">
        <button type="button" @click="onOkClick()" class="btn btn-outline-primary btn-sm text-capitalize-first">
          {{ helper.trans('button.save') }}
        </button>
      </LoaderWrapper>
    </template>
  </b-modal>
</template>

<script lang="ts">
import {defineComponent, ref} from 'vue'
import {reactive, toRefs} from "@vue/reactivity";
import {BModal} from "bootstrap-vue-3";
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import FormInputWrapper from "@/components/FormInputWrapper.vue";
import {helper} from "@/service/CommonHelper";
import {useNotification} from "@kyvg/vue3-notification";
import APIClient from "@/service/APIClient";
import ExtendedDealDTO from "@/models/ExtendedDealDTO";
import ValidationErrorDTO from "@/models/ValidationErrorDTO";

const api = new APIClient();
const notification = useNotification();

export default defineComponent({
  components: {
    BModal,
    LoaderWrapper,
    FormInputWrapper,
  },
  setup() {
    const loader = ref<InstanceType<typeof LoaderWrapper> | null>(null)
    const state = reactive({
      deal: null as ExtendedDealDTO | null,
      onChanged: null as null | (() => Promise<void>),
      errors: [] as ValidationErrorDTO[],
      form: {
        amount: '',
      },
    })

    const show = (deal: ExtendedDealDTO, onChanged: () => Promise<void>) => {
      state.deal = deal
      state.onChanged = onChanged
      state.errors = []
      state.form.amount = deal.takerSum.amount
    }

    const onOkClick = async () => {
      if (!state.deal) {
        return
      }

      state.errors = []
      loader.value?.show()
      try {
        await api.call.traderDeals.changeAmount(state.deal.id, state.form.amount)
        notification.notify({type: 'success', title: helper.trans('success.payout_amount_changed')})
        const done = state.onChanged
        state.deal = null
        await done?.()
      } catch (e) {
        if (helper.isValidationErrors(e)) {
          state.errors = helper.getValidationErrors(e)
          return
        }
        notification.notify({type: 'error', title: helper.trans('server_errors.INTERNAL_ERROR')})
      } finally {
        loader.value?.hide()
      }
    }

    return {
      ...toRefs(state),
      onOkClick,
      show,
      loader,
      helper,
    }
  }
})
</script>
